import { useGetMember, usePatchMember } from '#api/member';
import { queryKeyFactory } from '#api/queryKeyFactory';
import { Text } from '#atoms';
import { Modal } from '#molecules';
import { useDashboard } from '#providers/Dashboard';
import { useToast } from '#providers/ToastProvider';
import { autoHyphen } from '#utils/autoHyphen';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ClientReigstrtaionInput } from 'src/shared/components/client-registration/ClientRegistrationInput';
import Select from 'src/shared/components/select/Select';
import CustomTabButton from '../CustomTabButton';
import { ModalButton, ModalButtonContainer, ModalContainer } from '../styles';

interface FixCustomerInfoProps {
  setIsModalOpen: (value: boolean) => void;
}
const FixCustomerInfo = ({ setIsModalOpen }: FixCustomerInfoProps) => {
  const sexTab = { 남성: 'MALE', 여성: 'FEMALE' };
  const [sex, setInfoSex] = useState<'MALE' | 'FEMALE' | null>(null);
  const [age, setAge] = useState<string | null>(null);
  const [name, setName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');

  const { customerDetail } = useDashboard();

  const ageOptions = Array.from({ length: 100 }, (_, i) => (i + 1).toString());

  const calculateAge = (birth: string) => {
    const today = new Date();
    const birthDate = new Date(birth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const month = today.getMonth() - birthDate.getMonth();
    if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age.toString();
  };

  const sessionPersonId = sessionStorage.getItem('personId');
  const personId = customerDetail.person
    ? customerDetail.person.id
    : sessionPersonId;

  const { data } = useGetMember(personId || '', {
    enabled: !!personId
  });

  useEffect(() => {
    if (data) {
      setAge(calculateAge(data.birthday));
      setName(data.name);
      setPhone(data.phone);
      setInfoSex(data.sex as 'MALE' | 'FEMALE');
    }
  }, [data]);

  const { showToast } = useToast();

  const queryClient = useQueryClient();

  const { mutate } = usePatchMember({
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeyFactory.GET_MEMBER(personId || ''));
      showToast({
        type: 'success',
        message: '회원정보가 수정되었습니다.'
      });
    },
    onError: () => {
      showToast({
        type: 'fail',
        message: '회원정보 수정에 실패했습니다.'
      });
    }
  });

  const onNameChange = (value: string) => {
    setName(value);
  };

  const onAgeChange = (value: string) => {
    setAge(value);
  };

  const onPhoneNumberChange = (value: string) => {
    setPhone(autoHyphen(value));
  };

  return (
    <Modal style={{ padding: '24px' }} onClose={() => setIsModalOpen(false)}>
      <div className="flex justify-between w-full">
        <Text textStyle="t3">기본정보 수정</Text>
      </div>
      <div className="w-full flex flex-col gap-8">
        <ModalContainer>
          <div style={{ display: 'flex', gap: '2px' }}>
            <Text>성함</Text>
          </div>
          <ClientReigstrtaionInput onChange={onNameChange} value={name} />
        </ModalContainer>
        <ModalContainer>
          <Text>나이</Text>
          <Select
            selectContainerStyle={{ height: '53px' }}
            width={'100%'}
            placeholder="선택"
            defaultValue={age || '나이 선택'}
            options={ageOptions}
            onChange={age => onAgeChange(age)}
          />
        </ModalContainer>
        <ModalContainer>
          <Text>성별</Text>
          <CustomTabButton
            defaultTab={sex ?? ''}
            tab={sexTab}
            onClick={tab => setInfoSex(tab)}
          />
        </ModalContainer>
        <ModalContainer>
          <div style={{ display: 'flex', gap: '2px' }}>
            <Text>연락처</Text>
          </div>
          <ClientReigstrtaionInput
            placeholder="-를 제외한 번호를 입력해주세요"
            onChange={onPhoneNumberChange}
            value={phone}
          />
        </ModalContainer>
      </div>
      <ModalButtonContainer>
        <ModalButton onClick={() => setIsModalOpen(false)}>취소</ModalButton>
        <ModalButton
          style={{ background: '#7750E9' }}
          onClick={() => {
            if (!personId) return;
            mutate({
              personId,
              data: {
                name,
                phone,
                age: parseInt(age ?? '0'),
                sex
              }
            });
            setIsModalOpen(false);
          }}
        >
          수정
        </ModalButton>
      </ModalButtonContainer>
    </Modal>
  );
};

export default FixCustomerInfo;
